import { Button } from "@/components/ui/button";
import { LocalRelative } from "@/components/ui/local-date";
import {
  type AlertState,
  type GrafanaAlert,
  type GrafanaRuleSummary,
  refreshGrafanaAlerts,
  severityTone,
  useGrafanaAlerts,
} from "@/lib/grafana-alerts";
import { cn } from "@/lib/utils";
import { Navigate, createFileRoute, useLoaderData } from "@tanstack/react-router";
import {
  CircleCheck,
  ExternalLink,
  Hourglass,
  LayoutDashboard,
  RefreshCw,
  Siren,
  TriangleAlert,
} from "lucide-react";
import { useState } from "react";

/** `/alerts` — the team's live view of Grafana alert rules. Read-only:
 *  silencing and editing stay in Grafana. */
export const Route = createFileRoute("/_app/alerts")({
  component: AlertsPage,
});

const TONE_BAR: Record<ReturnType<typeof severityTone>, string> = {
  critical: "bg-red-600",
  high: "bg-orange-500",
  warning: "bg-amber-400",
  info: "bg-sky-400",
};

const TONE_CHIP: Record<ReturnType<typeof severityTone>, string> = {
  critical: "bg-red-600 text-white",
  high: "bg-orange-500 text-white",
  warning: "bg-amber-200 text-amber-900 dark:bg-amber-500/25 dark:text-amber-200",
  info: "bg-sky-100 text-sky-900 dark:bg-sky-500/20 dark:text-sky-200",
};

const SECTIONS: Array<{
  state: AlertState;
  title: string;
  hint: string;
  Icon: React.ElementType;
  heading: string;
}> = [
  {
    state: "firing",
    title: "Firing",
    hint: "The condition holds right now and Grafana has notified.",
    Icon: Siren,
    heading: "text-red-600 dark:text-red-400",
  },
  {
    state: "pending",
    title: "Pending",
    hint: "The condition holds, but not yet for as long as the rule waits before firing.",
    Icon: Hourglass,
    heading: "text-amber-600 dark:text-amber-400",
  },
  {
    state: "error",
    title: "Broken rules",
    hint: "Grafana couldn't evaluate these — they can't warn about anything until fixed.",
    Icon: TriangleAlert,
    heading: "text-orange-600 dark:text-orange-400",
  },
];

function AlertsPage() {
  const { isCustomer } = useLoaderData({ from: "/_app" });
  const { report, error } = useGrafanaAlerts(!isCustomer);
  const [refreshing, setRefreshing] = useState(false);

  if (isCustomer) return <Navigate to="/" />;

  async function refresh() {
    setRefreshing(true);
    try {
      await refreshGrafanaAlerts(true);
    } finally {
      setRefreshing(false);
    }
  }

  const alerts = report?.alerts ?? [];
  const firing = alerts.filter((a) => a.state === "firing");
  const grafanaUrl = report?.servers[0]?.url;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold">Alerts</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Live from Grafana
            {report && (
              <>
                {" · updated "}
                <LocalRelative value={report.fetchedAt} />
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {grafanaUrl && (
            <a
              href={`${grafanaUrl}/alerting/list`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm hover:bg-accent"
            >
              <ExternalLink className="h-4 w-4" /> Grafana
            </a>
          )}
          <Button variant="outline" size="sm" onClick={refresh} disabled={refreshing}>
            <RefreshCw className={cn("mr-1.5 h-4 w-4", refreshing && "animate-spin")} />
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <Notice>
          Couldn't reach Hub's alert endpoint: {error}. Showing the last answer, if any.
        </Notice>
      )}
      {report?.servers
        .filter((s) => !s.ok)
        .map((s) => (
          <Notice key={s.url}>
            {s.url} didn't answer ({s.error})
            {s.staleSince ? (
              <>
                {" — its alerts below are from "}
                <LocalRelative value={s.staleSince} />.
              </>
            ) : (
              " — its alerts are unknown."
            )}
          </Notice>
        ))}

      {!report ? (
        !error && <p className="text-sm text-muted-foreground">Loading…</p>
      ) : report.servers.length === 0 ? (
        <p className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">
          No Grafana is connected. Hub reads alerts from the Grafana behind log triage — set{" "}
          <code>GRAFANA_DISCOVER_URL</code> or add a log source.
        </p>
      ) : (
        <>
          <StatusHero firing={firing} rules={report.rules} alerts={alerts} />
          {SECTIONS.map((section) => {
            const rows = alerts.filter((a) => a.state === section.state);
            if (rows.length === 0) return null;
            return (
              <section key={section.state} data-testid={`alerts-${section.state}`}>
                <h2
                  className={cn(
                    "flex items-center gap-2 font-heading text-lg font-semibold",
                    section.heading,
                  )}
                >
                  <section.Icon className="h-5 w-5" />
                  {section.title}
                  <span className="text-sm font-normal text-muted-foreground">({rows.length})</span>
                </h2>
                <p className="mb-3 text-xs text-muted-foreground">{section.hint}</p>
                <div className="space-y-3">
                  {rows.map((a) => (
                    <AlertCard key={a.key} alert={a} />
                  ))}
                </div>
              </section>
            );
          })}
          <RuleInventory rules={report.rules} />
        </>
      )}
    </div>
  );
}

function Notice({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-amber-300/60 bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">
      {children}
    </div>
  );
}

function StatusHero({
  firing,
  rules,
  alerts,
}: {
  firing: GrafanaAlert[];
  rules: GrafanaRuleSummary[];
  alerts: GrafanaAlert[];
}) {
  const pending = alerts.filter((a) => a.state === "pending").length;
  const broken = alerts.filter((a) => a.state === "error").length;
  const critical = firing.filter((a) => a.severity === "critical").length;

  const tiles = [
    { label: "Firing", value: firing.length, hot: firing.length > 0, tone: "text-red-600" },
    { label: "Pending", value: pending, hot: pending > 0, tone: "text-amber-600" },
    { label: "Broken rules", value: broken, hot: broken > 0, tone: "text-orange-600" },
    { label: "Rules watched", value: rules.length, hot: false, tone: "" },
  ];

  return (
    <div className="space-y-3">
      {firing.length > 0 ? (
        <div
          data-testid="alerts-hero"
          className="flex items-center gap-4 rounded-xl bg-red-600 px-5 py-4 text-white shadow-md"
        >
          <Siren className="h-8 w-8 shrink-0 animate-pulse" />
          <div>
            <div className="text-xl font-bold">
              {firing.length === 1 ? "1 alert is firing" : `${firing.length} alerts are firing`}
            </div>
            <div className="text-sm text-white/85">
              {critical > 0 ? `${critical} critical · ` : ""}
              {[...new Set(firing.map((a) => a.folder))].join(", ")}
            </div>
          </div>
        </div>
      ) : (
        <div
          data-testid="alerts-hero"
          className="flex items-center gap-4 rounded-xl border border-emerald-300/60 bg-emerald-50 px-5 py-4 text-emerald-900 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200"
        >
          <CircleCheck className="h-8 w-8 shrink-0" />
          <div>
            <div className="text-xl font-bold">All clear</div>
            <div className="text-sm opacity-80">Nothing is firing across {rules.length} rules.</div>
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {tiles.map((t) => (
          <div key={t.label} className="rounded-lg border bg-card px-4 py-3">
            <div className="text-xs text-muted-foreground">{t.label}</div>
            <div className={cn("text-2xl font-bold tabular-nums", t.hot && t.tone)}>{t.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Chip({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium",
        className ?? "bg-muted text-muted-foreground",
      )}
    >
      {children}
    </span>
  );
}

function AlertCard({ alert }: { alert: GrafanaAlert }) {
  const tone = severityTone(alert.severity);
  return (
    <div
      data-testid="alert-card"
      className={cn(
        "flex overflow-hidden rounded-lg border bg-card shadow-sm",
        alert.state === "firing" && "border-red-300 dark:border-red-500/40",
      )}
    >
      <div className={cn("w-1.5 shrink-0", TONE_BAR[tone])} />
      <div className="min-w-0 flex-1 space-y-2 px-4 py-3">
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <div className="min-w-0 font-semibold">
            {alert.name}
            {alert.subject && <span className="text-muted-foreground"> — {alert.subject}</span>}
          </div>
          {alert.since && (
            <div className="shrink-0 text-xs text-muted-foreground">
              since <LocalRelative value={alert.since} />
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {alert.severity && <Chip className={TONE_CHIP[tone]}>{alert.severity}</Chip>}
          {alert.environment && <Chip>{alert.environment}</Chip>}
          {alert.service && <Chip>{alert.service}</Chip>}
          <Chip>{alert.folder}</Chip>
          {alert.value && <Chip className="bg-muted font-mono text-foreground">{alert.value}</Chip>}
        </div>
        {alert.summary && <p className="text-sm">{alert.summary}</p>}
        {alert.error && (
          <p className="break-words font-mono text-xs text-orange-700 dark:text-orange-300">
            {alert.error}
          </p>
        )}
        {alert.description && <p className="text-xs text-muted-foreground">{alert.description}</p>}
        <div className="flex flex-wrap gap-4 pt-1 text-xs">
          {alert.ruleUrl && (
            <a
              href={alert.ruleUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-primary hover:underline"
            >
              <ExternalLink className="h-3.5 w-3.5" /> Rule in Grafana
            </a>
          )}
          {alert.dashboardUrl && (
            <a
              href={alert.dashboardUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-primary hover:underline"
            >
              <LayoutDashboard className="h-3.5 w-3.5" /> Dashboard
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

const DOT: Record<GrafanaRuleSummary["state"], string> = {
  firing: "bg-red-600",
  pending: "bg-amber-400",
  error: "bg-orange-500",
  ok: "bg-emerald-500",
  paused: "bg-muted-foreground/40",
};

function RuleInventory({ rules }: { rules: GrafanaRuleSummary[] }) {
  const folders = [...new Set(rules.map((r) => r.folder))];
  if (rules.length === 0) return null;
  return (
    <details className="rounded-lg border bg-card" data-testid="alerts-rules">
      <summary className="cursor-pointer select-none px-4 py-3 text-sm font-medium">
        All rules ({rules.length})
      </summary>
      <div className="space-y-4 border-t px-4 py-3">
        {folders.map((folder) => (
          <div key={folder}>
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {folder}
            </div>
            <ul className="divide-y">
              {rules
                .filter((r) => r.folder === folder)
                .map((r) => (
                  <li key={r.key} className="flex items-center gap-3 py-1.5 text-sm">
                    <span
                      title={r.state}
                      className={cn("h-2 w-2 shrink-0 rounded-full", DOT[r.state])}
                    />
                    <span className="min-w-0 flex-1 truncate">
                      {r.ruleUrl ? (
                        <a
                          href={r.ruleUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:underline"
                        >
                          {r.name}
                        </a>
                      ) : (
                        r.name
                      )}
                    </span>
                    {r.instances > 1 && (
                      <span className="text-xs text-muted-foreground">{r.instances} targets</span>
                    )}
                    {r.severity && <Chip>{r.severity}</Chip>}
                  </li>
                ))}
            </ul>
          </div>
        ))}
      </div>
    </details>
  );
}
